import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays,
  CalendarClock,
  Send,
  BarChart3,
  MessageSquare,
  AudioLines,
  FileText,
  FolderKanban,
  ContactRound,
  Sparkles,
  Shield,
  Users,
  Zap,
  ArrowRight,
  Check,
  Globe,
  MessageCircle,
  ChevronDown,
  Menu,
  X,
  Clock,
  MousePointerClick,
  Eye,
  Rocket,
  HelpCircle,
  Building2,
  Mail,
  Target,
  Search,
  TrendingUp,
  Megaphone,
  PenTool,
  Layers,
  MapPin,
  Bot,
  Newspaper,
  Image,
  Crosshair,
  Activity,
  Upload,
  LayoutDashboard,
  Workflow,
} from 'lucide-react';
import { brand } from '@iheart/shared';
import { GradientText, MotionButton } from '@iheart/ui';
import { PublicFooter } from '../components/PublicFooter';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useAuth } from '../lib/auth';

const spring = brand.motion.spring;

/* ── Hero rotating phrases ────────────────────────────────────────── */

const HERO_PHRASES = [
  'Social Scheduling.',
  'Digital Marketing.',
  'Content Planning.',
  'PR Listening.',
  'AI Research.',
  'Brand Management.',
  'Ads Management.',
  'Content Analysis.',
  'SEO & GEO Optimisation.',
  'Campaign Strategy.',
  'Website Analysis.',
  'Sales CRM.',
  'Project Management.',
  'Ads Analysis.',
  'Web User Analysis.',
  'Digital PR.',
  'Media Outreach.',
];

function useTypewriter(phrases: string[], typingMs = 65, deletingMs = 35, pauseMs = 1800) {
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting' | 'waiting'>('typing');

  useEffect(() => {
    const phrase = phrases[idx] ?? '';
    let timer: number;

    switch (phase) {
      case 'typing':
        if (text.length < phrase.length) {
          timer = window.setTimeout(() => setText(phrase.slice(0, text.length + 1)), typingMs);
        } else {
          setPhase('pausing');
        }
        break;
      case 'pausing':
        timer = window.setTimeout(() => setPhase('deleting'), pauseMs);
        break;
      case 'deleting':
        if (text.length > 0) {
          timer = window.setTimeout(() => setText(text.slice(0, -1)), deletingMs);
        } else {
          setPhase('waiting');
        }
        break;
      case 'waiting':
        timer = window.setTimeout(() => {
          setIdx((i) => (i + 1) % phrases.length);
          setPhase('typing');
        }, 300);
        break;
    }

    return () => clearTimeout(timer);
  }, [text, idx, phase, phrases, typingMs, deletingMs, pauseMs]);

  return text;
}

/* ── Data ──────────────────────────────────────────────────────────── */

const NAV_LINKS = [
  { label: 'Home', href: '#' },
  { label: 'Features', href: '#features' },
  { label: 'Why Us', href: '#why-us' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Platforms', href: '#platforms' },
  { label: 'About', href: '#about' },
  { label: 'FAQ', href: '#faq' },
];

const FEATURES = [
  {
    icon: LayoutDashboard,
    title: 'Social Media Management',
    desc: 'Run every social account from one place — schedule and queue posts, tailor the copy per platform, save drafts, route them through team approvals, and auto-publish across all your networks. Your whole social presence, on autopilot.',
  },
  {
    icon: CalendarDays,
    title: 'Content Calendar',
    desc: 'Plan, drag-and-drop, and visualise your entire content schedule across every platform in a beautiful calendar view.',
  },
  {
    icon: PenTool,
    title: 'Content Planning & Bulk Scheduling',
    desc: 'Draft a whole campaign in a Google-Docs-style planner, then mass-schedule a week, fortnight, or month of posts at once — with trend discovery surfacing content ideas while they\'re hot.',
  },
  {
    icon: Send,
    title: 'Multi-Platform Publishing',
    desc: 'Compose once and publish to Facebook, Instagram, Threads, X, LinkedIn (personal profiles and Company Pages), YouTube, and Google Business Profile at once — with TikTok and WhatsApp coming soon.',
  },
  {
    icon: MapPin,
    title: 'Google Business & Local Listings',
    desc: 'Manage your Google Business Profile — posts, reviews, business info, and insights — to strengthen your presence in local search.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Insights',
    desc: 'Track engagement, reach, impressions, and follower growth with unified cross-platform dashboards and visual reports.',
  },
  {
    icon: Activity,
    title: 'Website Behaviour & Heatmaps',
    desc: 'Connect your Microsoft Clarity account to bring your website visitors into the picture — heatmaps, scroll depth, clicks, and session recordings sit alongside your social, PR, and ad data, so you finally see the full marketing journey in one place.',
  },
  {
    icon: Search,
    title: 'SEO, GEO & Keyword Ranking',
    desc: 'Track your Google Search keyword rankings over time, discover new keyword ideas, and measure your GEO visibility — how often AI assistants like ChatGPT recommend your brand.',
  },
  {
    icon: MessageSquare,
    title: 'Engage & Respond',
    desc: 'Manage comments, DMs, mentions and reviews from your connected accounts in a single unified inbox.',
  },
  {
    icon: AudioLines,
    title: 'PR & Media Listening',
    desc: 'Monitor brand mentions, press coverage, keywords, and industry conversations across news and the web — plus social platforms (X, Instagram, TikTok, YouTube, Reddit) via the Social Listening add-on — with a built-in journalist & media directory.',
  },
  {
    icon: Mail,
    title: 'Media Relations & Pitching',
    desc: 'Build media lists from a shared South African journalist directory, then run your outreach through a tracked pitch pipeline — from draft to sent to secured coverage — so no opportunity slips through. Pitching to journalists is intended for registered PR professionals who already have an established media list.',
  },
  {
    icon: Upload,
    title: 'Offline & Online Media Data',
    desc: 'Upload print, online, TV, radio and podcast coverage (e.g. Newsclip) via CSV — all media types supported. It flows into PR Coverage and your reports with AVE and sentiment.',
  },
  {
    icon: Image,
    title: 'Media Library & AI Images',
    desc: 'One home for every image and video — uploaded or generated by Domina AI — ready to drop into posts by filename. Media is kept for 30 days.',
  },
  {
    icon: FolderKanban,
    title: 'Project Management & Tasks',
    desc: 'Kanban boards, task assignments, deadlines, and team collaboration — with push notifications so nothing falls through the cracks.',
  },
  {
    icon: CalendarClock,
    title: 'Planner & Google Calendar Sync',
    desc: 'Map out your team\'s work in agenda, day, week, or month views — and push tasks straight to Google Calendar, so deadlines show up wherever you already plan your day.',
  },
  {
    icon: ContactRound,
    title: 'CRM & Sales Pipeline',
    desc: 'Track leads, manage deals through visual pipeline stages, and convert social engagement into real revenue with a built-in sales CRM.',
  },
  {
    icon: FileText,
    title: 'Reports & Exports',
    desc: 'Generate branded PDF and CSV reports for clients, stakeholders, or your own records with one click.',
  },
  {
    icon: Sparkles,
    title: 'Domina AI Assistant',
    desc: 'Your AI co-pilot drafts captions and hashtags, reads your uploaded files (images, PDFs, docs), generates images, schedules posts for you, and answers product and support questions — right inside the app.',
  },
  {
    icon: MessageCircle,
    title: 'Team Chat & Huddles',
    desc: 'Built-in team messaging with private DMs, group chats, and a floating chat widget on every page — keep your team connected without leaving the app.',
  },
  {
    icon: Globe,
    title: 'Link in Bio',
    desc: 'Create a branded micro-landing page with custom links, social icons, and built-in click analytics.',
  },
  {
    icon: MousePointerClick,
    title: 'Short Links & Click Analytics',
    desc: 'Shorten and brand your links with built-in UTM tracking, then see exactly who clicked — total clicks, devices, and location down to country and city — in a dedicated dashboard and your reports.',
  },
  {
    icon: Users,
    title: 'Multi-Workspace',
    desc: 'Manage multiple brands or clients from one account — each with their own team members, social accounts, and settings.',
  },
];

const PLATFORMS: { name: string; comingSoon?: boolean }[] = [
  { name: 'Facebook' },
  { name: 'Instagram' },
  { name: 'Threads' },
  { name: 'X (Twitter)' },
  { name: 'LinkedIn' },
  { name: 'YouTube' },
  { name: 'TikTok', comingSoon: true },
  { name: 'WhatsApp', comingSoon: true },
  { name: 'Google Business' },
];

function PlatformLogo({ name }: { name: string }) {
  const cls = 'size-8';
  switch (name) {
    case 'Facebook':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case 'Instagram':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    case 'Threads':
      // iHeart brand asset (black on transparent) — same logo as the dashboard
      // and PDF reports, so Threads is consistent everywhere.
      return <img src="/threads-logo.png" alt="Threads" className={`${cls} object-contain`} />;
    case 'X (Twitter)':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case 'LinkedIn':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case 'YouTube':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case 'TikTok':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
        </svg>
      );
    case 'WhatsApp':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={cls}>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    case 'Google Business':
      return (
        <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className={cls}>
          <path d="M6 16L10 6h28l4 10" />
          <path d="M6 16c0 3.3 2.7 6 6 6s6-2.7 6-6" />
          <path d="M18 16c0 3.3 2.7 6 6 6s6-2.7 6-6" />
          <path d="M30 16c0 3.3 2.7 6 6 6s6-2.7 6-6" />
          <rect x="6" y="22" width="36" height="20" rx="3" />
          <text x="30" y="39" textAnchor="middle" fontSize="15" fontFamily="Arial,sans-serif" fontWeight="700" fill="currentColor" stroke="none">G</text>
        </svg>
      );
    default:
      return <Globe className={cls} />;
  }
}

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: MousePointerClick,
    title: 'Connect Your Accounts',
    desc: 'Link your social media profiles in seconds. We support Facebook, Instagram, Threads, X, LinkedIn (personal profiles and Company Pages), YouTube, TikTok, WhatsApp, and Google Business Profile.',
  },
  {
    step: '02',
    icon: CalendarDays,
    title: 'Plan & Schedule Content',
    desc: 'Use the visual content calendar to plan your posts. Compose once, customise per platform, and schedule for the perfect time.',
  },
  {
    step: '03',
    icon: Rocket,
    title: 'Publish Everywhere',
    desc: 'Your content goes live automatically across all connected platforms. Sit back and watch your engagement grow.',
  },
  {
    step: '04',
    icon: Eye,
    title: 'Analyse & Optimise',
    desc: 'Track performance with analytics and scheduled reports. See what works, learn from insights, and refine your strategy.',
  },
];

const PRICING = [
  {
    name: 'Free Trial',
    pricePrefix: 'Try it Out!',
    price: 'Free',
    period: '14 days',
    desc: 'Try iHeart CNTRL risk-free for 14 days — no credit card required.',
    highlight: false,
    cta: 'Register for Early Access',
    features: [
      '1 workspace (1 brand)',
      'Social media scheduling (Facebook & Instagram)',
      '10 scheduled posts',
      'Content calendar & planner',
      'Link in Bio page',
      'Domina Agent (non-AI) — free to use',
      'Domina AI – add token top-ups to use',
    ],
    excluded: [
      'No included AI chats or images',
      'No analytics',
      'No team chat',
    ],
  },
  {
    name: 'Professional',
    pricePrefix: 'For Only',
    price: 'R799',
    period: '/month',
    desc: 'For growing businesses ready to run their whole brand from one place.',
    highlight: true,
    cta: 'Get Started',
    features: [
      'Social media management — schedule, queue & auto-publish to all platforms',
      '1 workspace = 1 brand · 1 user',
      'Extra users — R399/month each',
      'Extra workspaces — R799/month each',
      '20 scheduled posts/month',
      'Content calendar & Content Planner',
      'Link in Bio page',
      'Domina Agent (non-AI) — free to use',
      'Domina AI – token top-ups (pay as you go)',
    ],
    excluded: [
      'Full analytics & reports — Agency',
      'PR & media relations + media listening — Agency',
      'Offline & online media data (AVE & sentiment) — Agency',
      'Engage inbox, CRM & project management — Agency',
      'Media library + AI image generation — Agency',
      'Short links + UTM click analytics — Agency',
      'Team chat & huddles — Agency',
      'Included Domina AI chats & images — Agency',
      'SEO, GEO & keyword ranking — Agency',
      'Website behaviour & heatmaps — Agency',
      'Google Business Profile management — Agency',
    ],
  },
  {
    name: 'Agency',
    pricePrefix: 'Per user · per workspace',
    price: 'R9,999',
    period: '/mo',
    desc: 'R9,999 per user per workspace — from R75,000/mo for multiple users and workspaces. Contact us for a custom package.',
    highlight: false,
    cta: 'Contact Sales',
    features: [
      'R9,999 per user / workspace',
      'From R75,000/mo for multiple users & workspaces',
      'Everything in Professional, plus:',
      'All social platforms supported',
      'Planner + Google Calendar sync',
      'Full analytics & reports',
      'PR & media relations — directory + pitching (for registered PR professionals with an existing media list)',
      'Media listening',
      'Offline & online media data (AVE & sentiment)',
      'Media library + AI image generation',
      'Short links + UTM click analytics',
      'Engage inbox',
      'CRM & sales pipeline',
      'Project management (Kanban) & tasks',
      'Team chat & huddles (live colleague chat)',
      'Google Business Profile management',
      'SEO, GEO & keyword ranking',
      'Website behaviour & heatmaps (Microsoft Clarity)',
      'Automated reports & exports',
      'Domina Agent (non-AI) — free to use',
      'Domina AI – 20 chats + 20 images/month included, plus token top-ups',
      'Team roles & permissions',
      'Dedicated account manager',
      'Priority support',
      'API access',
      'Need more? Contact sales for a custom package',
    ],
  },
];

const FAQ = [
  {
    q: 'What social media platforms does iHeart CNTRL support?',
    a: 'We currently support Facebook, Instagram, Threads, X (Twitter), LinkedIn (both personal profiles and Company Pages), YouTube, and Google Business Profile — with TikTok, WhatsApp and more being added regularly.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes! Every new account gets a 14-day free trial with access to Facebook and Instagram, 10 scheduled posts, a content calendar, and a Link in Bio page — no credit card required.',
  },
  {
    q: 'Can I manage multiple clients or brands?',
    a: 'Absolutely. With our Professional and Agency plans, you can create multiple workspaces — each with their own social accounts, team members, and settings. Perfect for agencies.',
  },
  {
    q: 'What is Domina — and what is free vs paid?',
    a: 'Domina comes in two forms. The Domina Agent (non-AI) is free on every plan — a built-in guide that answers product and support questions, gives you a tour, and helps you find your way around, using no AI tokens. Domina AI is the paid, token-powered assistant: she drafts captions and hashtags, reads files you upload (images, PDFs, docs), generates images and schedules posts for you. Domina AI runs on included monthly chats/images on the Agency plan, or pay-as-you-go token top-ups on any plan.',
  },
  {
    q: 'How does billing work?',
    a: 'Plans are billed monthly in South African Rand (ZAR). Professional starts at R799/month per workspace, and Agency plans are tailored to your needs — contact our sales team for a custom quote. Cancel anytime — no long-term contracts.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. We use industry-standard encryption, secure OAuth connections for all social platforms, and never store your social media passwords. Your data stays yours.',
  },
  {
    q: 'Can I try iHeart CNTRL before committing?',
    a: 'Absolutely — sign up for a 14-day free trial to explore the platform with Facebook and Instagram. When you\'re ready for more platforms and features, upgrade to Professional or Agency.',
  },
];

const COMING_SOON_FEATURES = [
  {
    icon: Image,
    title: 'Digital Asset Management',
    desc: 'Organise, tag, and manage all your brand images, videos, and creative assets in one centralised media library.',
  },
  {
    icon: Megaphone,
    title: 'Ads Management',
    desc: 'Create, manage, and optimise ad campaigns across Facebook, Instagram, and other social platforms from a single dashboard.',
  },
  {
    icon: Newspaper,
    title: 'Blog & Website Content Ideas',
    desc: 'AI-generated content ideas for your blog, services pages, and website copy — based on what your audience is searching for.',
  },
  {
    icon: TrendingUp,
    title: 'Marketing & Campaign Planning',
    desc: 'Plan end-to-end marketing campaigns with timelines, budgets, deliverables, and performance tracking all in one place.',
  },
  {
    icon: Bot,
    title: 'AI-Powered Strategy',
    desc: 'Get AI recommendations on what to post, when to post, which platforms to focus on, and what services to offer your audience.',
  },
  {
    icon: Crosshair,
    title: 'Full-Funnel Campaign Attribution',
    desc: 'Track every click from AI-planned content to published ad to website visit — first-click, last-click, and every touchpoint in between, with privacy-safe anonymous and identified user tracking.',
  },
  {
    icon: Workflow,
    title: 'AI Content Automation',
    desc: 'Go beyond ideas — Domina AI writes full blog articles, social captions, and ad copy, then publishes directly to your website or social accounts. Requires a connected paid AI model.',
  },
  {
    icon: LayoutDashboard,
    title: 'Holistic Brand Analytics',
    desc: 'The world\'s first complete analysis dashboard — combining AI discovery, campaign performance, ad attribution, website behaviour, and offline data into one unified view for ultimate brand understanding.',
  },
];

function ComingSoonToggle() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group mx-auto flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-6 py-3 shadow-sm transition-all hover:border-pink-300 hover:shadow-md"
      >
        <span className="rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          Coming Soon
        </span>
        <span className="text-sm font-medium text-neutral-600">
          {COMING_SOON_FEATURES.length} features in development
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <ChevronDown className="size-4 text-neutral-400 transition-colors group-hover:text-pink-500" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-5 pt-8 sm:grid-cols-2 lg:grid-cols-3">
              {COMING_SOON_FEATURES.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...spring, delay: i * 0.04 }}
                  className="relative rounded-2xl border border-neutral-200/60 bg-neutral-50/50 p-6 shadow-sm"
                >
                  <span className="absolute right-4 top-4 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    Coming Soon
                  </span>
                  <div
                    className="mb-4 inline-flex size-10 items-center justify-center rounded-xl text-white opacity-60"
                    style={{ backgroundImage: brand.gradient.primary }}
                  >
                    <item.icon className="size-5" />
                  </div>
                  <h3 className="text-base font-semibold text-neutral-800">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Component ─────────────────────────────────────────────────────── */

export function HomeRoute() {
  useDocumentTitle('Social Scheduling. Total Control.');
  const { status } = useAuth();
  const isAuthed = status === 'authenticated';
  const [mobileNav, setMobileNav] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const typedText = useTypewriter(HERO_PHRASES);

  // Marketing home is viewable by everyone. The nav CTA adapts: logged-in users
  // get a "Go to Dashboard" button; logged-out users get "Log in" / "Register".
  // (No auto-redirect — the Terms/Privacy "Back to App" links straight to
  // /dashboard, which sends logged-out users to the login page via RequireAuth.)

  return (
    <div className="min-h-screen bg-white">
      {/* ── Top Nav ────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-neutral-100 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="iHeart CNTRL" className="size-9 object-contain" />
            <span className="text-lg font-bold tracking-tight text-neutral-800">
              iHeart <GradientText>CNTRL</GradientText>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-pink-500"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {isAuthed ? (
              <Link to="/dashboard">
                <MotionButton size="sm">Go to Dashboard</MotionButton>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden text-sm font-medium text-neutral-600 transition-colors hover:text-pink-500 sm:block"
                >
                  Log in
                </Link>
                <Link to="/register">
                  <MotionButton size="sm">Register Free</MotionButton>
                </Link>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileNav(!mobileNav)}
              className="flex size-9 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-50 lg:hidden"
              aria-label="Toggle menu"
            >
              {mobileNav ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        <AnimatePresence>
          {mobileNav && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-t border-neutral-100 bg-white lg:hidden"
            >
              <nav className="flex flex-col px-5 py-3">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileNav(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-pink-500"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="mt-2 flex flex-col gap-2 border-t border-neutral-100 pt-3">
                  <Link
                    to="/login"
                    onClick={() => setMobileNav(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
                  >
                    Log in
                  </Link>
                  <Link to="/register" onClick={() => setMobileNav(false)}>
                    <MotionButton size="sm" className="w-full">Register Free</MotionButton>
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-5 pb-20 pt-16 sm:pt-24">
        <div
          className="pointer-events-none absolute -left-60 -top-60 size-[600px] rounded-full opacity-[0.07] blur-3xl"
          style={{ backgroundImage: brand.gradient.primary }}
        />
        <div
          className="pointer-events-none absolute -bottom-60 -right-60 size-[600px] rounded-full opacity-[0.07] blur-3xl"
          style={{ backgroundImage: brand.gradient.primary }}
        />

        <div className="relative mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.1 }}
          >
            <div
              className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-lg"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <Sparkles className="size-3.5" />
              AI-Powered, Human-Inspired 👇🏻 This is The Way
            </div>
          </motion.div>

          <motion.h1
            className="text-4xl font-extrabold leading-tight tracking-tight text-neutral-900 sm:text-5xl md:text-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.2 }}
          >
            <span className="block h-[1.25em]">
              {typedText}
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
                className="ml-0.5 inline-block h-[0.85em] w-[3px] translate-y-[0.08em] rounded-sm bg-neutral-800 align-middle"
              />
            </span>
            <GradientText>Total Control.</GradientText>
          </motion.h1>

          <motion.p
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-neutral-500 sm:text-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.3 }}
          >
            Your all-in-one digital marketing control centre — schedule and publish across many
            social platforms, manage campaigns, ads, SEO, GEO, CRM, and performance analytics from
            one beautiful dashboard. Made by South African industry professionals who know exactly
            what agencies, brands, and growing businesses <em className="font-semibold text-neutral-700">really</em> need. This app is for working professionals who need more control, better insight and less admin.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.4 }}
          >
            <Link to="/register">
              <MotionButton size="lg" className="gap-2 px-8">
                Register for Early Access <ArrowRight className="size-4" />
              </MotionButton>
            </Link>
            <a
              href="#pricing"
              className="text-sm font-medium text-neutral-500 transition-colors hover:text-pink-500"
            >
              View pricing &darr;
            </a>
          </motion.div>

          {/* Trust pills */}
          <motion.div
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            {[
              { icon: Zap, text: 'Save hours every week' },
              { icon: Shield, text: 'No credit card required' },
              { icon: Clock, text: '14-day free trial' },
            ].map((b) => (
              <div
                key={b.text}
                className="flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 shadow-sm"
              >
                <b.icon className="size-4 text-pink-500" />
                <span className="font-medium">{b.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Platforms ───────────────────────────────────────────── */}
      <section id="platforms" className="border-t border-neutral-100 bg-neutral-50 px-5 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
            Connect Your Accounts
          </h2>
          <p className="mt-2 text-2xl font-bold text-neutral-800 sm:text-3xl">
            One dashboard. <GradientText>Many platforms.</GradientText>
          </p>
          <p className="mx-auto mt-3 max-w-lg text-neutral-500">
            Connect all your social media accounts and manage everything from a single place. No more switching between apps.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {PLATFORMS.map((p) => (
              <motion.div
                key={p.name}
                whileHover={{ scale: 1.05, y: -2 }}
                transition={spring}
                className="relative flex flex-col items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-5 shadow-sm"
              >
                {p.comingSoon && (
                  <span className="absolute right-2 top-2 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                    Soon
                  </span>
                )}
                <div className={`text-neutral-700 ${p.comingSoon ? 'opacity-50' : ''}`}>
                  <PlatformLogo name={p.name} />
                </div>
                <span className={`text-sm font-semibold ${p.comingSoon ? 'text-neutral-400' : 'text-neutral-700'}`}>{p.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────── */}
      <section id="how-it-works" className="px-5 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-14 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              How It Works
            </h2>
            <p className="mt-2 text-3xl font-bold text-neutral-800 sm:text-4xl">
              Up and running in <GradientText>4 simple steps</GradientText>
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...spring, delay: i * 0.1 }}
                className="relative text-center"
              >
                <div
                  className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl text-white shadow-lg"
                  style={{ backgroundImage: brand.gradient.primary }}
                >
                  <item.icon className="size-6" />
                </div>
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-pink-400">
                  Step {item.step}
                </span>
                <h3 className="text-base font-semibold text-neutral-800">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ──────────────────────────────────────── */}
      <section id="features" className="border-t border-neutral-100 bg-neutral-50 px-5 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              Features
            </h2>
            <p className="mt-2 text-3xl font-bold text-neutral-800 sm:text-4xl">
              Everything you need to <GradientText>dominate &amp; control your brand</GradientText>
            </p>
            <p className="mx-auto mt-3 max-w-xl text-neutral-500">
              From planning and publishing to analytics, engagement, PR, CRM and team
              collaboration — iHeart CNTRL is your all-in-one digital control centre.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ ...spring, delay: i * 0.04 }}
                className="group rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div
                  className="mb-4 inline-flex size-10 items-center justify-center rounded-xl text-white"
                  style={{ backgroundImage: brand.gradient.primary }}
                >
                  <f.icon className="size-5" />
                </div>
                <h3 className="text-base font-semibold text-neutral-800">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Sets Us Apart ────────────────────────────────── */}
      <section id="why-us" className="relative overflow-hidden px-5 py-20">
        <div
          className="pointer-events-none absolute -right-40 top-0 size-[500px] rounded-full opacity-[0.05] blur-3xl"
          style={{ backgroundImage: brand.gradient.primary }}
        />
        <div className="relative mx-auto max-w-5xl">
          <div className="mb-14 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              Why iHeart CNTRL
            </h2>
            <p className="mt-2 text-3xl font-bold text-neutral-800 sm:text-4xl">
              More than a scheduler — your <GradientText>complete digital control centre</GradientText>
            </p>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-neutral-500">
              You already get far more than scheduling — content planning &amp; bulk scheduling, PR
              &amp; media listening, Google Business, CRM &amp; sales pipeline, project management and
              team chat are all <strong className="text-neutral-700">live today</strong>. And we're not
              stopping there: full-funnel campaign attribution, website heatmaps, offline data, and
              everything in between are on the way. One login. Total control.
            </p>
          </div>

          {/* Coming Soon toggle */}
          <ComingSoonToggle />

          <div className="mt-12 text-center">
            <p className="text-neutral-500">
              We're constantly building new features.{' '}
              <a
                href="mailto:hello@iheartdigital.co.za"
                className="font-medium text-pink-500 underline decoration-pink-300 underline-offset-2 hover:text-pink-600"
              >
                Tell us what you need
              </a>{' '}
              and we'll prioritise it.
            </p>
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────── */}
      <section id="pricing" className="px-5 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-14 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              Pricing
            </h2>
            <p className="mt-2 text-3xl font-bold text-neutral-800 sm:text-4xl">
              Simple, transparent <GradientText>pricing</GradientText>
            </p>
            <p className="mx-auto mt-3 max-w-lg text-neutral-500">
              Start free and scale as you grow. No hidden fees, no surprises. All prices in South African Rand.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {PRICING.map((plan) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={spring}
                className={`relative flex flex-col rounded-2xl border p-7 ${
                  plan.highlight
                    ? 'border-pink-200 bg-white shadow-xl shadow-pink-500/10 ring-2 ring-pink-400'
                    : 'border-neutral-200 bg-white shadow-sm'
                }`}
              >
                {plan.highlight && (
                  <div
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-bold text-white"
                    style={{ backgroundImage: brand.gradient.primary }}
                  >
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-bold text-neutral-800">{plan.name}</h3>
                <div className="mt-3">
                  {'pricePrefix' in plan && (plan as { pricePrefix?: string }).pricePrefix && (
                    <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-400">
                      {(plan as { pricePrefix?: string }).pricePrefix}
                    </span>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-neutral-900">{plan.price}</span>
                    {plan.period && (
                      <span className="text-sm text-neutral-400">{plan.period}</span>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-sm text-neutral-500">{plan.desc}</p>
                <ul className="mt-6 flex flex-1 flex-col gap-2.5">
                  {plan.features.map((feature) => {
                    const isSoon = feature.includes('(Coming Soon)');
                    const label = feature.replace(' (Coming Soon)', '');
                    return (
                      <li key={feature} className={`flex items-start gap-2 text-sm ${isSoon ? 'text-neutral-400' : 'text-neutral-600'}`}>
                        <Check className={`mt-0.5 size-4 shrink-0 ${isSoon ? 'text-amber-400' : 'text-emerald-500'}`} />
                        <span className="flex flex-wrap items-center gap-1.5">
                          {label}
                          {isSoon && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">
                              Soon
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                  {'excluded' in plan && (plan as { excluded?: string[] }).excluded?.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-neutral-400">
                      <X className="mt-0.5 size-4 shrink-0 text-neutral-300" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to={`/register?plan=${plan.name === 'Free Trial' ? 'free_trial' : plan.name === 'Professional' ? 'professional' : 'agency'}`} className="mt-8 block">
                  {plan.highlight ? (
                    <MotionButton size="lg" className="w-full">
                      {plan.cta}
                    </MotionButton>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={spring}
                      className="w-full rounded-xl border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-neutral-700 transition-colors hover:border-pink-300 hover:text-pink-600"
                    >
                      {plan.cta}
                    </motion.button>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Band ───────────────────────────────────────────── */}
      <section className="px-5 py-16">
        <div
          className="mx-auto max-w-4xl rounded-3xl px-8 py-14 text-center shadow-xl sm:px-16"
          style={{ backgroundImage: brand.gradient.primary }}
        >
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to take control?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-white/80">
            Join teams and agencies already using iHeart CNTRL to streamline their social
            media workflow. Start your 14-day free trial today.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link to="/register">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={spring}
                className="rounded-xl bg-white px-8 py-3 text-sm font-semibold text-pink-600 shadow-lg transition-colors hover:bg-neutral-50"
              >
                Register for Early Access
              </motion.button>
            </Link>
            <a
              href="mailto:hello@iheartdigital.co.za"
              className="flex items-center gap-2 text-sm font-medium text-white/80 transition-colors hover:text-white"
            >
              <Mail className="size-4" />
              Contact sales
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <Check className="size-4" /> No credit card required
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="size-4" /> 14-day free trial
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="size-4" /> Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section id="faq" className="border-t border-neutral-100 bg-neutral-50 px-5 py-20">
        <div className="mx-auto max-w-2xl">
          <div className="mb-12 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              FAQ
            </h2>
            <p className="mt-2 text-3xl font-bold text-neutral-800 sm:text-4xl">
              Frequently asked <GradientText>questions</GradientText>
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-50"
                >
                  <span className="flex items-start gap-3">
                    <HelpCircle className="mt-0.5 size-4 shrink-0 text-pink-400" />
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`size-4 shrink-0 text-neutral-400 transition-transform ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-4 pl-12 text-sm leading-relaxed text-neutral-500">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ──────────────────────────────────────────────── */}
      <section id="about" className="px-5 py-20">
        <div className="mx-auto max-w-4xl">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                About Us
              </h2>
              <p className="mt-2 text-3xl font-bold text-neutral-800">
                Built by <GradientText>iHeart Digital</GradientText>
              </p>
              <p className="mt-4 leading-relaxed text-neutral-500">
                iHeart CNTRL is a wholly owned product of{' '}
                <a
                  href="https://iheartdigital.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-pink-500 underline decoration-pink-300 underline-offset-2 hover:text-pink-600"
                >
                  IHEART DIGITAL (PTY) LTD
                </a>
                , a company registered in South Africa (Reg. 2026/453657/07), headquartered in Johannesburg.
              </p>
              <p className="mt-3 leading-relaxed text-neutral-500">
                iHeart CNTRL is your all-in-one tool for scheduling, planning, and strategising
                across <strong className="text-neutral-700">social media management</strong>,{' '}
                <strong className="text-neutral-700">digital brand management</strong>, and{' '}
                <strong className="text-neutral-700">digital performance marketing &amp; analysis</strong>.
                Built from the ground up to give businesses, freelancers, and agencies the tools
                they need to take total control of their digital presence — without the steep learning curve.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="https://iheartdigital.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:border-pink-300 hover:text-pink-500"
                >
                  <Building2 className="size-4" />
                  Visit iHeart Digital
                </a>
                <a
                  href="mailto:hello@iheartdigital.co.za"
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:border-pink-300 hover:text-pink-500"
                >
                  <Mail className="size-4" />
                  hello@iheartdigital.co.za
                </a>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div
                className="flex size-48 items-center justify-center rounded-3xl shadow-xl sm:size-56"
                style={{ backgroundImage: brand.gradient.primary }}
              >
                <img
                  src="/logo.png"
                  alt="iHeart CNTRL"
                  className="size-32 object-contain drop-shadow-lg sm:size-40"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="border-t border-neutral-100 bg-neutral-50">
        <PublicFooter />
      </div>
    </div>
  );
}
